import React, { FC, useState, useEffect, useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import { SelectMultiple } from 'tea-component';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { attachTagTooltips } from './attachTagTooltips';
import { buildSelectFilterAccepter } from '@intelligence/utils/commonUtils';
import './index.scss';
import CommonBubble from '@home/components/CommonBubble';
interface SensorTowerTagsSelectProps {
  title: string;
  options: { text: string; value: string; textCN: string }[];
  value: string[];
  filterKey?: string;
  width?: number;
  className?: string;
  onChange: (value: string[]) => void;
  isEmptySelectAll?: boolean;
  // 筛选器Button文案前面的图标，默认没有图标
  icon?: string;
}
const SensorTowerTagsSelect: FC<SensorTowerTagsSelectProps> = (props) => {
  const {
    options = [],
    value,
    onChange,
    className,
    title,
    width,
    filterKey,
    isEmptySelectAll = false,
    icon,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [selectedTag, setSelectedTag] = useState<string[]>([]);
  const [displayOptions, setDisplayOptions] = useState<any[]>([]);
  const [enableTooltip, setEnableTooltip] = useState<boolean>(true);
  const optionsWithTooltip = useMemo(() => {
    if (!enableTooltip) return options;
    return attachTagTooltips(filterKey, options, locale);
  }, [enableTooltip, filterKey, locale, options]);
  useEffect(() => {
    const valueTmp = Array.isArray(value) ? value : [];
    if (isEmptySelectAll) {
      setSelectedTag(['All']);
    } else {
      setSelectedTag(valueTmp);
    }
  }, [value, isEmptySelectAll]);
  useEffect(() => {
    setDisplayOptions(optionsWithTooltip);
  }, [optionsWithTooltip]);
  useEffect(() => {
    if (!enableTooltip) {
      setEnableTooltip(true);
    }
  }, [selectedTag, enableTooltip]);

  // 创建filter函数，用于SelectMultiple组件的内部搜索
  const tagOptionsFilterAcceptor = useMemo(() => {
    return buildSelectFilterAccepter({
      separate: false,
      getOptionText: (option: any) => {
        // 根据locale选择正确的文本字段进行搜索匹配
        // 注意：这里必须返回字符串类型，因为text可能是React元素（有tooltip时）
        const getStringValue = (val: any): string => {
          if (typeof val === 'string') return val;
          if (val === null || val === undefined) return '';
          return String(val?.value || '');
        };
        const defaultOptionText = getStringValue(option?.text) || getStringValue(option?.value) || '';
        switch (locale) {
          case LANGUAGE_CN: {
            const textCN = option?.textCN;
            return typeof textCN === 'string' ? textCN : defaultOptionText;
          }
          default: {
            const textEN = option?.text;
            return typeof textEN === 'string' ? textEN : defaultOptionText;
          }
        }
      },
    });
  }, [locale]);
  const handleSelectAll = () => {
    const values = optionsWithTooltip?.map((item) => item?.value);
    if (!selectedTag?.length) {
      setSelectedTag(values);
      onChange?.(values);
    } else {
      setSelectedTag([]);
      onChange?.([]);
    }
    setEnableTooltip(false);
  };
  const titleNow = useMemo(() => {
    if (!selectedTag?.length)
      return (
        <span className="dbi-intelligence-tag-select-placeholder">
          {formatMessage({ id: 'dbi_select_any_tag' })}
        </span>
      );
    if (selectedTag?.length === optionsWithTooltip.length) return formatMessage({ id: 'dbi_all' });
    if (selectedTag?.length === 1) {
      if (locale === LANGUAGE_CN) {
        const targetTag = optionsWithTooltip?.find((item) => item?.value === selectedTag[0]);
        if (targetTag) return targetTag?.textCN;
      } else {
        return selectedTag[0];
      }
    }
    return selectedTag?.length;
  }, [selectedTag, locale, optionsWithTooltip]);
  const targetOptions = useMemo(() => {
    return displayOptions?.map((item) => {
      const text = locale === LANGUAGE_CN ? item?.textCN : item?.text;

      // If option has a tooltip, wrap with Bubble component for centered positioning
      if (item?.tooltip) {
        return {
          value: item.value,
          textCN: item.textCN,
          text: (
            <div className="dbi-tag-option-wrapper">
              <CommonBubble
                dark
                placement="top"
                content={item.tooltip}
                className="dbi-tag-tooltip-bubble"
              >
                <div className="dbi-tag-option-text">{text}</div>
              </CommonBubble>
            </div>
          ),
          label: text, // Keep plain text for accessibility
        };
      }

      // No tooltip, return plain text
      return {
        value: item.value,
        textCN: item.textCN,
        text,
      };
    });
  }, [displayOptions, locale]);
  return (
    <SelectMultiple
      className={classnames('select-item dbi-intelligence-tag-select', className)}
      overlayClassName="dbi-top-genre-market-select-overlay"
      staging={false}
      size="m"
      searchable
      matchButtonWidth
      appearance="button"
      options={targetOptions}
      // 使用filter prop让SelectMultiple内部处理搜索，支持中英文
      filter={tagOptionsFilterAcceptor}
      style={{ width: width || 270 }}
      searchPlaceholder={formatMessage({ id: 'selector_multiple_component_placeholder' })}
      button={(option) => (
        <div className="select-button">
          <div className="select-button-text">
            {icon && <img src={icon} alt="" className="select-button-icon" />}
            {formatMessage({ id: title })}
          </div>
          <div className="dbi-select-flex">{titleNow}</div>
        </div>
      )}
      tips={
        <div className="dbi-selects-flex">
          <span className="dbi-selects-flex-lf">{formatMessage({ id: title })}</span>
          <span className="dbi-selects-flex-rt" onClick={() => handleSelectAll()}>
            {!selectedTag?.length
              ? formatMessage({ id: 'dbi_select_all' })
              : formatMessage({ id: 'dbi_clear_all' })}
          </span>
        </div>
      }
      value={selectedTag}
      onChange={(value) => {
        setSelectedTag(value);
        onChange?.(value);
        setEnableTooltip(false);
      }}
      onClose={() => {
        if (value?.length) {
          onChange?.(value);
          return;
        }
      }}
    />
  );
};
export default SensorTowerTagsSelect;
